import { ComponentProps, forwardRef, ReactNode, Ref, useCallback, useMemo, useState } from 'react';
import { css, CSSObject } from '@emotion/react';
import { ScheduledSms } from '@weave/schema-gen-ts/dist/schemas/messaging/scheduled/shared/v1/models.pb';
import { OutboundMessageStatus } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { useTranslation } from '@frontend/i18n';
import { useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  Text,
  AutoGrowTextarea,
  InlineNotification,
  KeyNames,
  ButtonVariants,
  useTooltip,
} from '@frontend/design-system';
import { useThreadDropzone } from '../hooks/use-thread-dropzone';
import type { UseThreadMediaResponse } from '../hooks/use-thread-media';
import { EditScheduledMessageForm, SendingAreaForm } from '../types';
import {
  MAX_CHARACTER_LIMIT,
  SHORTENED_URL_TOOLTIP_MESSAGE,
  CHARACTER_LIMIT_EXCEEDED_MESSAGE,
  ERROR_NOTIFICATION_MESSAGE,
} from '../utils/constants';
import { getErrorNotificationMessage } from '../utils/get-error-message-utils';
import { getSendButtonDisabledReason } from '../utils/get-send-button-disabled-reason';
import { SEND_BUTTON_CLASS, SendButton, type SendButtonActions } from './buttons/send-button';
import { MediaPreviewList } from './media-preview-list';
import { ScheduledMessageList } from './scheduled-messages/scheduled-message-list';
import { ShortenedUrlHighlightOverlay } from './shortened-url-highlight-overlay';
import { SignatureInput } from './signature-input';
import { SMSTypingIndicator } from './sms-typing-indicator';
import { styles } from './thread-sending-area.styles';

const ErrorTooltipContent = ({ messages }: { messages: string[] }) => {
  const { t } = useTranslation('thread-sending-area');

  if (messages.length === 1) {
    return <>{messages[0]}</>;
  }

  return (
    <>
      <div css={styles.errorTooltipHeaderStyles}>{t(ERROR_NOTIFICATION_MESSAGE)}</div>
      <ul css={styles.errorTooltipListStyles}>
        {messages.map((msg, idx) => (
          <li
            key={idx}
            css={css`
              margin-bottom: ${idx < messages.length - 1 ? 'var(--fabric-number-n-1-5)' : 0};
            `}
          >
            {msg}
          </li>
        ))}
      </ul>
    </>
  );
};
export type ThreadSendingAreaState = {
  threadId: string;
  groupId: string;
  isGroupThread: boolean;
  formState: SendingAreaForm;
  editScheduledMessageFormState: EditScheduledMessageForm;
  disabled: boolean;
  canSendSMS: boolean;
  scheduledMessages: ScheduledSms[];
  onSelectScheduledMessage: (messageId?: string) => void;
  selectedScheduledMessage?: ScheduledSms;
  ref: Ref<HTMLTextAreaElement>;
  textareaId: string;
  showSignature: boolean;
  hasShortenedUrl: boolean;
  offendingUrl: string | null;
  offendingUrlIndex: number | null;
  /** Call this before form submission when send is triggered by Enter key press */
  markSendAsEnterTriggered: () => void;
} & SendButtonActions &
  Pick<ComponentProps<typeof SendButton>, 'primaryButtonTrackingPrefix' | 'buttonType'> &
  Omit<UseThreadMediaResponse, 'resetLocalMedia'>;

type ThreadSendingAreaProps = Omit<ThreadSendingAreaState, 'ref'> & {
  autoGrowTextareaProps?: Pick<ComponentProps<typeof AutoGrowTextarea>, 'minRows' | 'maxRows' | 'disabled'>;
  children?: ReactNode;
  canSchedule?: boolean;
  sendButtonVariant?: ButtonVariants;
  sendButtonPosition?: 'top' | 'bottom';
  mediaPreviewListMaxHeight?: CSSObject['maxHeight'];
  scheduledMessageListMaxHeight?: CSSObject['maxHeight'];
  hideTypingIndicator?: boolean;
  placeholderText?: string;
  isCompact?: boolean;
};

export const ThreadSendingArea = forwardRef<HTMLTextAreaElement, ThreadSendingAreaProps>(
  (
    {
      threadId,
      groupId,
      isGroupThread,
      formState,
      editScheduledMessageFormState,
      media,
      removeMedia,
      addMedia,
      disabled,
      onSend,
      onSendScheduled,
      onSchedule,
      onReschedule,
      onDeleteScheduledMessage,
      autoGrowTextareaProps,
      children,
      canSchedule = true,
      placeholderText,
      scheduledMessages,
      onSelectScheduledMessage,
      selectedScheduledMessage,
      textareaId,
      sendButtonVariant,
      sendButtonPosition = 'bottom',
      showSignature,
      mediaPreviewListMaxHeight,
      scheduledMessageListMaxHeight,
      primaryButtonTrackingPrefix,
      hideTypingIndicator,
      buttonType,
      hasShortenedUrl,
      offendingUrl,
      offendingUrlIndex,
      markSendAsEnterTriggered,
      isCompact = false,
    },
    ref
  ) => {
    const { t } = useTranslation('thread-sending-area');
    const [wiggleCount, setWiggleCount] = useState(0);
    const [textareaScrollTop, setTextareaScrollTop] = useState(0);
    const showInlineNotificationLabel = useMatchMedia({ minWidth: 978, maxWidth: 1024 });
    const {
      tooltipProps: errorTooltipProps,
      triggerProps: errorTriggerProps,
      Tooltip: ErrorTooltip,
    } = useTooltip({ placement: 'top' });

    const dropzoneState = useThreadDropzone({ addMedia });

    const textareaFieldProps = selectedScheduledMessage
      ? editScheduledMessageFormState.getFieldProps('body')
      : formState.getFieldProps('body');

    const charCount = (textareaFieldProps.value ?? '').length;
    const isOverCharLimit = charCount > MAX_CHARACTER_LIMIT;

    // Determine which errors exist
    const hasError = isOverCharLimit || hasShortenedUrl;

    // Build error messages for tooltip
    const errorMessages = useMemo(() => {
      const messages = [];
      if (isOverCharLimit) {
        messages.push(t(CHARACTER_LIMIT_EXCEEDED_MESSAGE));
      }
      if (hasShortenedUrl) {
        messages.push(t(SHORTENED_URL_TOOLTIP_MESSAGE));
      }
      return messages;
    }, [isOverCharLimit, hasShortenedUrl, t]);

    // Get the disabled reason for aria-describedby
    const disabledReasonInfo = useMemo(() => {
      return getSendButtonDisabledReason(
        {
          hasShortenedUrl,
          isOverCharLimit,
          isFormIncomplete: selectedScheduledMessage
            ? !editScheduledMessageFormState.isComplete
            : !formState.isComplete,
        },
        t
      );
    }, [
      hasShortenedUrl,
      isOverCharLimit,
      selectedScheduledMessage,
      editScheduledMessageFormState.isComplete,
      formState.isComplete,
      t,
    ]);

    const handleKeyUp = (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (!e.shiftKey && e.key === KeyNames.Enter) {
        if (hasShortenedUrl || isOverCharLimit) {
          setWiggleCount((c) => c + 1);
          return;
        }
        if (isOverCharLimit) return;

        // Mark this send as triggered by Enter key for analytics tracking
        markSendAsEnterTriggered();

        if (selectedScheduledMessage) {
          editScheduledMessageFormState.formProps.onSubmit(e);
          return;
        }
        formState.formProps.onSubmit(e);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (!e.shiftKey && e.key === KeyNames.Enter) {
        e.preventDefault();
      }
    };

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        const clipboardItems = e.clipboardData?.items;
        const pastedText = e.clipboardData?.getData('text')?.trim();

        if (!clipboardItems) return;

        const files = Array.from(clipboardItems).reduce<File[]>((acc, curr) => {
          if (curr.type.includes('image')) {
            const file = curr.getAsFile();
            if (file) acc.push(file);
          }
          return acc;
        }, []);

        if (!pastedText) {
          e.preventDefault();
        }

        addMedia(files);
      },
      [addMedia]
    );

    const formEditingStyles = useMemo(() => {
      switch (selectedScheduledMessage?.status) {
        case OutboundMessageStatus.SCHEDULED:
          return css({
            backgroundColor: theme.colors.warning5,
            textarea: { backgroundColor: theme.colors.warning5 },
            [`button[aria-disabled="true"]:not(.${SEND_BUTTON_CLASS}), button[aria-disabled="false"]:not(.${SEND_BUTTON_CLASS}), button:hover:not(&[aria-disabled="true"]):not(.${SEND_BUTTON_CLASS})`]:
              {
                transition: 'background-color 0ms',
                ':hover, :focus': {
                  backgroundColor: theme.colors.warning10,
                },
              },
          });
        case OutboundMessageStatus.PAUSED:
          return css({
            backgroundColor: theme.colors.neutral5,
            textarea: { backgroundColor: theme.colors.neutral5 },
            [`button[aria-disabled="true"]:not(.${SEND_BUTTON_CLASS}), button[aria-disabled="false"]:not(.${SEND_BUTTON_CLASS}), button:hover:not(&[aria-disabled="true"]):not(.${SEND_BUTTON_CLASS})`]:
              {
                transition: 'background-color 0ms',
                ':hover, :focus': {
                  backgroundColor: theme.colors.neutral10,
                },
              },
          });
        default:
          return undefined;
      }
    }, [selectedScheduledMessage?.status]);

    return (
      <>
        {!hideTypingIndicator && <SMSTypingIndicator threadId={threadId} groupId={groupId} />}
        <ScheduledMessageList
          scheduledMessages={scheduledMessages}
          onSelectScheduledMessage={onSelectScheduledMessage}
          selectedScheduledMessage={selectedScheduledMessage}
          listMaxHeight={scheduledMessageListMaxHeight}
        />
        <MediaPreviewList
          media={media}
          removeMedia={removeMedia}
          css={{
            backgroundColor: selectedScheduledMessage ? theme.colors.warning5 : undefined,
            maxHeight: mediaPreviewListMaxHeight ?? 200,
          }}
        />
        <div {...dropzoneState.getRootProps()}>
          <form
            css={[styles.formBaseStyles, formEditingStyles]}
            onKeyUp={handleKeyUp}
            onKeyDown={handleKeyDown}
            {...(selectedScheduledMessage ? editScheduledMessageFormState.formProps : formState.formProps)}
          >
            <div css={styles.flexContainerStyles}>
              <div css={styles.textareaWrapperStyles}>
                {hasShortenedUrl && offendingUrl !== null && offendingUrlIndex !== null && (
                  <ShortenedUrlHighlightOverlay
                    text={textareaFieldProps.value ?? ''}
                    offendingUrl={offendingUrl}
                    offendingUrlIndex={offendingUrlIndex}
                    scrollTop={textareaScrollTop}
                  />
                )}
                <AutoGrowTextarea
                  ref={ref}
                  css={[styles.textareaBaseStyles, disabled && styles.textareaDisabledBgStyles]}
                  placeholder={placeholderText || t('Type something...')}
                  {...textareaFieldProps}
                  {...autoGrowTextareaProps}
                  minRows={autoGrowTextareaProps?.minRows ?? 4}
                  maxRows={autoGrowTextareaProps?.maxRows ?? 4}
                  disabled={disabled}
                  id={textareaId}
                  onPaste={handlePaste}
                  onScroll={(e) => {
                    setTextareaScrollTop(e.currentTarget.scrollTop);
                  }}
                  data-testid='thread-sending-area'
                />
              </div>
              {sendButtonPosition === 'top' && (
                <SendButton
                  canSchedule={canSchedule}
                  isGroupThread={isGroupThread}
                  disabled={
                    isOverCharLimit ||
                    hasShortenedUrl ||
                    (selectedScheduledMessage ? !editScheduledMessageFormState.isComplete : !formState.isComplete)
                  }
                  variant={sendButtonVariant}
                  onSend={onSend}
                  onSendScheduled={onSendScheduled}
                  onSchedule={onSchedule}
                  onReschedule={onReschedule}
                  onDeleteScheduledMessage={onDeleteScheduledMessage}
                  scheduledMessageForEdit={selectedScheduledMessage}
                  primaryButtonTrackingPrefix={primaryButtonTrackingPrefix}
                  buttonType={buttonType}
                />
              )}
            </div>
            <div css={styles.signatureRowStyles}>
              {showSignature && (
                <SignatureInput
                  {...formState.getFieldProps('signature')}
                  onUnmount={() => {
                    if (typeof ref !== 'function') ref?.current?.focus();
                  }}
                />
              )}
            </div>
            <div
              css={[
                styles.bottomActionsBaseStyles,
                {
                  justifyContent: sendButtonPosition === 'top' ? 'start' : children ? 'space-between' : 'end',
                },
              ]}
            >
              {children}
              {sendButtonPosition === 'bottom' && (
                <div css={styles.sendButtonGroupStyles}>
                  {hasError && (
                    <>
                      <div
                        key={wiggleCount}
                        css={wiggleCount > 0 ? styles.wiggleStyles : undefined}
                        {...errorTriggerProps}
                      >
                        <InlineNotification
                          status='error'
                          size='small'
                          message={getErrorNotificationMessage({
                            isCompact,
                            showInlineNotificationLabel,
                            isOverCharLimit,
                            hasShortenedUrl,
                            charCount,
                          })}
                          hideBorder
                        />
                      </div>
                      <ErrorTooltip {...errorTooltipProps}>
                        <ErrorTooltipContent messages={errorMessages} />
                      </ErrorTooltip>
                    </>
                  )}
                  {disabledReasonInfo && (
                    <span id={disabledReasonInfo.descriptionId} style={{ display: 'none' }}>
                      {disabledReasonInfo.message}
                    </span>
                  )}
                  <SendButton
                    canSchedule={canSchedule}
                    isGroupThread={isGroupThread}
                    disabled={
                      isOverCharLimit ||
                      hasShortenedUrl ||
                      (selectedScheduledMessage ? !editScheduledMessageFormState.isComplete : !formState.isComplete)
                    }
                    variant={sendButtonVariant}
                    onSend={onSend}
                    onSendScheduled={onSendScheduled}
                    onSchedule={onSchedule}
                    onReschedule={onReschedule}
                    onDeleteScheduledMessage={onDeleteScheduledMessage}
                    scheduledMessageForEdit={selectedScheduledMessage}
                    primaryButtonTrackingPrefix={primaryButtonTrackingPrefix}
                    buttonType={buttonType}
                    size={isCompact ? 'small' : undefined}
                    ariaDescribedBy={disabledReasonInfo?.descriptionId}
                  />
                </div>
              )}
            </div>
            {!!dropzoneState.isDragActive && (
              <div css={styles.dropzoneOverlayStyles}>
                <div css={styles.dropzoneInnerStyles}>
                  <Text css={{ textAlign: 'center' }} color='light'>
                    {t('Drop image here (PNG or JPG files only)')}
                  </Text>
                </div>
              </div>
            )}
          </form>
        </div>
        <input {...dropzoneState.getInputProps()} css={styles.dropzoneInputStyles} />
      </>
    );
  }
);
ThreadSendingArea.displayName = 'ThreadSendingArea';
